// TUINITY: command menu for the AI bot — commands shown PER CLIENT (account).
//
// In Halo each client is a separate ACCOUNT/workspace (the top-left switcher):
// CEOSA, SimpleWallet, Tuinity, ... The menu shows the universal commands plus
// any extra commands defined for the currently active account.
//
// Commands are posted to the conversation as PRIVATE NOTES; the external bot
// reads them and acts. This is purely a UI helper so non-technical staff can
// click a command instead of typing it by hand.
//
// ───────────────────────── HOW TO EDIT ─────────────────────────
//  • Commands for EVERY client → edit UNIVERSAL_COMMANDS below.
//  • Commands for ONE client   → add an entry in SCOPE_COMMANDS_BY_ACCOUNT,
//      keyed by the ACCOUNT NAME in lowercase and without spaces.
//      Example: account "Simple Wallet"  →  key "simplewallet".
//  • Each item shape:
//      { id, label, description, command, needsInput, inputPlaceholder? }
//        label/description → what staff see in the menu
//        command           → the EXACT text that gets sent (must match the bot)
//        needsInput        → true shows a text box for an extra instruction/amount
// ────────────────────────────────────────────────────────────────

// Shown for every client.
const UNIVERSAL_COMMANDS = [
  {
    id: 'status',
    label: 'Ver estado',
    description: 'Muestra el estado de la conversación.',
    command: '!status',
    needsInput: false,
  },
  {
    id: 'summary',
    label: 'Resumen de la conversación',
    description: 'El bot genera un resumen en español.',
    command: '!summary',
    needsInput: false,
  },
  {
    id: 'stop',
    label: 'Pausar bot',
    description: 'El bot deja de responder en esta conversación.',
    command: '!stop',
    needsInput: false,
  },
  {
    id: 'start',
    label: 'Reactivar bot',
    description: 'El bot vuelve a responder automáticamente.',
    command: '!start',
    needsInput: false,
  },
  {
    id: 'type',
    label: 'Hacer que el bot escriba ahora',
    description: 'El bot escribe un mensaje al cliente ahora mismo.',
    command: '!type',
    needsInput: true,
    inputPlaceholder:
      'Instrucción (opcional). Ej: dile que su pedido ya está en camino',
  },
  {
    id: 'followup_on',
    label: 'Activar follow-ups',
    description: 'Activa los seguimientos automáticos.',
    command: '!followup on',
    needsInput: false,
  },
  {
    id: 'followup_off',
    label: 'Desactivar follow-ups',
    description: 'Desactiva los seguimientos automáticos.',
    command: '!followup off',
    needsInput: false,
  },
  {
    id: 'followup_now',
    label: 'Enviar follow-up ahora',
    description: 'Dispara un seguimiento inmediato.',
    command: '!followup now',
    needsInput: true,
    inputPlaceholder: 'Instrucción (opcional)',
  },
];

// Extra commands per client (account). Key = account name, lowercase, no spaces.
const SCOPE_COMMANDS_BY_ACCOUNT = {
  // SimpleWallet: tienda Shopify + pagos TiloPay.
  simplewallet: [
    {
      id: 'tilo',
      label: 'Generar link de pago (TiloPay)',
      description: 'Crea un link de pago TiloPay para esta conversación.',
      command: '!tilo',
      needsInput: true,
      inputPlaceholder: 'Monto (opcional). Ej: 49.99',
    },
    {
      id: 'purchase',
      label: 'Marcar venta como pagada',
      description: 'Marca la venta como pagada y avisa el evento a Meta.',
      command: '!purchase',
      needsInput: true,
      inputPlaceholder: 'Monto (opcional). Ej: 49.99',
    },
  ],

  // CEOSA (centro ortopédico): solo comandos universales → no necesita entrada.
  // ceosa: [],

  // Agregar más clientes acá. Ejemplos:
  // fluffysharkss: [ /* ... */ ],
  // tuinity: [ /* ... */ ],
};

const normalizeAccountName = name =>
  (name || '').toLowerCase().replace(/\s+/g, '');

// Returns the command list for a given account name (universal + per-client).
export function getCommandsForAccount(accountName) {
  const extra =
    SCOPE_COMMANDS_BY_ACCOUNT[normalizeAccountName(accountName)] || [];
  return [...UNIVERSAL_COMMANDS, ...extra];
}

export default getCommandsForAccount;
